import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiX, FiRefreshCw, FiClock } from "react-icons/fi";
import salaryMasterService from "../../services/salaryMasterService";
import employeeService from "../../services/employeeService";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;
const round2 = (n) => Math.round(Number(n) * 100) / 100;

// Today as YYYY-MM-DD (default effectiveFrom)
const todayStr = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM = {
    employeeId: "",
    effectiveFrom: todayStr(),
    basicPay: "",
    da: "",
    hra: "",
    otherAllowances: "",
    pf: "",
    pt: "",
    tds: "",
    otherDeductions: "",
    notes: "",
};

function Field({ label, name, value, onChange, hint, required, error }) {
    return (
        <div className="form-group">
            <label className="form-label">
                {label}{required && <span style={{ color: "var(--danger)" }}> *</span>}
            </label>
            <input
                type="number" min="0" step="0.01"
                name={name} value={value} onChange={onChange}
                className={`form-control ${error ? "error" : ""}`}
                placeholder="0"
            />
            {hint && <span className="form-hint">{hint}</span>}
            {error && <span className="form-error">{error}</span>}
        </div>
    );
}

export default function SalaryMasterForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prefilledEmpId = searchParams.get("employeeId") || "";
    const isEdit = Boolean(id);

    const [form, setForm] = useState({ ...EMPTY_FORM, employeeId: prefilledEmpId });
    const [employee, setEmployee] = useState(null);   // selected employee info
    const [employees, setEmployees] = useState([]);     // all employees for dropdown
    const [history, setHistory] = useState([]);     // past revisions for this employee
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEdit);

    // ── Load all employees for the dropdown ────────────────────────────────
    useEffect(() => {
        employeeService.getAll({ limit: 200, status: "Active" })
            .then((res) => setEmployees(res.data))
            .catch((err) => toast.error(err.message));
    }, []);

    // ── Load existing record in edit mode ──────────────────────────────────
    useEffect(() => {
        if (!isEdit) return;
        salaryMasterService.getById(id)
            .then((res) => {
                const d = res.data;
                setForm({
                    employeeId: d.employee._id,
                    effectiveFrom: d.effectiveFrom,
                    basicPay: d.basicPay,
                    da: d.da,
                    hra: d.hra,
                    otherAllowances: d.otherAllowances,
                    pf: d.pf,
                    pt: d.pt,
                    tds: d.tds,
                    otherDeductions: d.otherDeductions,
                    notes: d.notes,
                });
                setEmployee(d.employee);
            })
            .catch((err) => { toast.error(err.message); navigate("/salary-master"); })
            .finally(() => setFetching(false));
    }, [id, isEdit, navigate]);

    // ── When employee is selected, fetch their revision history ────────────
    useEffect(() => {
        const empId = form.employeeId;
        if (!empId) { setHistory([]); setEmployee(null); return; }

        const found = employees.find((e) => e._id === empId);
        if (found) setEmployee(found);

        salaryMasterService.getEmployeeHistory(empId)
            .then((res) => setHistory(res.data))
            .catch(() => setHistory([]));
    }, [form.employeeId, employees]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    // ── Live computed preview ─────────────────────────────────────────────
    const grossPay = round2((+form.basicPay || 0) + (+form.da || 0) + (+form.hra || 0) + (+form.otherAllowances || 0));
    const totalDeductions = round2((+form.pf || 0) + (+form.pt || 0) + (+form.tds || 0) + (+form.otherDeductions || 0));
    const estimatedNet = round2(grossPay - totalDeductions); // before LOP — just structure preview

    // ── Validate ──────────────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        if (!form.employeeId) errs.employeeId = "Employee is required.";
        if (!form.effectiveFrom) errs.effectiveFrom = "Effective from date is required.";
        if (!form.basicPay && form.basicPay !== 0) errs.basicPay = "Basic pay is required.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Submit ────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const payload = {
                employee: form.employeeId,
                effectiveFrom: form.effectiveFrom,
                basicPay: Number(form.basicPay) || 0,
                da: Number(form.da) || 0,
                hra: Number(form.hra) || 0,
                otherAllowances: Number(form.otherAllowances) || 0,
                pf: Number(form.pf) || 0,
                pt: Number(form.pt) || 0,
                tds: Number(form.tds) || 0,
                otherDeductions: Number(form.otherDeductions) || 0,
                notes: form.notes,
            };

            if (isEdit) {
                const res = await salaryMasterService.update(id, payload);
                toast.success(res.message);
            } else {
                const res = await salaryMasterService.create(payload);
                toast.success(res.message);
            }

            navigate("/salary-master");
        } catch (err) {
            toast.error(err.message || "Failed to save salary structure.");
        } finally {
            setLoading(false);
        }
    };

    if (fetching) return <div className="loading-center"><div className="spinner" />Loading...</div>;

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Salary Structure" : "Add Salary Structure"}</h1>
                    <p className="page-subtitle">
                        {employee ? `${employee.fullName} · ${employee.designation}` : "Select an employee"}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "1rem", alignItems: "start" }}>

                    {/* ── Left: Form ── */}
                    <div style={{ display: "grid", gap: "1rem" }}>

                        {/* Employee & Date */}
                        <div className="card">
                            <div className="card-header">Employee & Effective Date</div>
                            <div className="card-body">
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Employee <span style={{ color: "var(--danger)" }}>*</span></label>
                                        <select
                                            name="employeeId"
                                            value={form.employeeId}
                                            onChange={handleChange}
                                            disabled={isEdit}
                                            className={`form-control ${errors.employeeId ? "error" : ""}`}
                                        >
                                            <option value="">Select employee...</option>
                                            {employees.map((emp) => (
                                                <option key={emp._id} value={emp._id}>
                                                    {emp.fullName} ({emp.employeeCode})
                                                </option>
                                            ))}
                                        </select>
                                        {errors.employeeId && <span className="form-error">{errors.employeeId}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">Effective From <span style={{ color: "var(--danger)" }}>*</span></label>
                                        <input
                                            type="date"
                                            name="effectiveFrom"
                                            value={form.effectiveFrom}
                                            onChange={handleChange}
                                            className={`form-control ${errors.effectiveFrom ? "error" : ""}`}
                                        />
                                        <span className="form-hint">The month this structure applies from</span>
                                        {errors.effectiveFrom && <span className="form-error">{errors.effectiveFrom}</span>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Earnings */}
                        <div className="card">
                            <div className="card-header">Earnings</div>
                            <div className="card-body">
                                <div className="form-grid">
                                    <Field label="Basic Pay (₹)" name="basicPay" value={form.basicPay} onChange={handleChange} required error={errors.basicPay} />
                                    <Field label="Dearness Allowance — DA (₹)" name="da" value={form.da} onChange={handleChange} />
                                    <Field label="House Rent Allowance — HRA (₹)" name="hra" value={form.hra} onChange={handleChange} />
                                    <Field label="Other Allowances (₹)" name="otherAllowances" value={form.otherAllowances} onChange={handleChange} />
                                </div>
                            </div>
                        </div>

                        {/* Deductions */}
                        <div className="card">
                            <div className="card-header">Fixed Monthly Deductions</div>
                            <div className="card-body">
                                <div className="form-grid">
                                    <Field label="Provident Fund — PF (₹)" name="pf" value={form.pf} onChange={handleChange} hint="Employee's share" />
                                    <Field label="Professional Tax — PT (₹)" name="pt" value={form.pt} onChange={handleChange} />
                                    <Field label="TDS (₹)" name="tds" value={form.tds} onChange={handleChange} />
                                    <Field label="Other Deductions (₹)" name="otherDeductions" value={form.otherDeductions} onChange={handleChange} />
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="card">
                            <div className="card-header">Notes (optional)</div>
                            <div className="card-body">
                                <textarea
                                    name="notes" value={form.notes}
                                    onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                                    rows={2} className="form-control" style={{ resize: "vertical" }}
                                    placeholder="e.g. Revised after annual appraisal — April 2025"
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                            <button type="button" className="btn btn-outline" onClick={() => navigate("/salary-master")}>
                                <FiX size={14} /> Cancel
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                                <FiSave size={14} /> {loading ? "Saving..." : isEdit ? "Update Structure" : "Save Structure"}
                            </button>
                        </div>
                    </div>

                    {/* ── Right: Live Preview + History ── */}
                    <div style={{ display: "grid", gap: "1rem" }}>

                        {/* Preview */}
                        <div className="card" style={{ position: "sticky", top: "1rem" }}>
                            <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                <FiRefreshCw size={13} /> Structure Preview
                            </div>
                            <div style={{ padding: "1rem" }}>
                                {[
                                    ["Basic Pay", form.basicPay || 0],
                                    ["DA", form.da || 0],
                                    ["HRA", form.hra || 0],
                                    ["Other Allowances", form.otherAllowances || 0],
                                ].map(([label, val]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: "1px solid var(--border)" }}>
                                        <span style={{ color: "var(--text-muted)" }}>{label}</span>
                                        <span>{inr(val)}</span>
                                    </div>
                                ))}
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                                    <span>Gross Pay</span>
                                    <span style={{ color: "#d97706" }}>{inr(grossPay)}</span>
                                </div>

                                <div style={{ marginTop: "0.75rem" }}>
                                    {[
                                        ["PF", form.pf || 0],
                                        ["PT", form.pt || 0],
                                        ["TDS", form.tds || 0],
                                        ["Other Deductions", form.otherDeductions || 0],
                                    ].map(([label, val]) => (
                                        <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: "1px solid var(--border)" }}>
                                            <span style={{ color: "var(--text-muted)" }}>{label}</span>
                                            <span style={{ color: Number(val) > 0 ? "#dc2626" : "var(--text-muted)" }}>{inr(val)}</span>
                                        </div>
                                    ))}
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 700, padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                                        <span>Fixed Deductions</span>
                                        <span style={{ color: "#dc2626" }}>{inr(totalDeductions)}</span>
                                    </div>
                                </div>

                                <div style={{ background: "#f0fdf4", borderRadius: 8, padding: "0.75rem", textAlign: "center", marginTop: "0.75rem" }}>
                                    <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>Est. Net (before LOP)</div>
                                    <div style={{ fontSize: 22, fontWeight: 800, color: "#16a34a" }}>{inr(estimatedNet)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Revision History */}
                        {history.length > 0 && (
                            <div className="card">
                                <div className="card-header" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                    <FiClock size={13} /> Revision History
                                </div>
                                <div>
                                    {history.map((h, i) => (
                                        <div
                                            key={h._id}
                                            style={{
                                                padding: "0.6rem 1rem",
                                                borderBottom: "1px solid var(--border)",
                                                background: i === 0 ? "var(--primary-light)" : "transparent",
                                            }}
                                        >
                                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                                                    {i === 0 && <span style={{ fontSize: 10, background: "var(--primary)", color: "#fff", padding: "1px 6px", borderRadius: 99, marginRight: 5 }}>Current</span>}
                                                    {h.effectiveFrom}
                                                </span>
                                                <span style={{ fontSize: 12.5, fontWeight: 700, color: "#d97706" }}>{inr(h.grossPay)}</span>
                                            </div>
                                            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2 }}>
                                                Basic: {inr(h.basicPay)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
}